// Abridor ÚNICO de IndexedDB — a estrutura que todos os bancos do app usam.
//
// ── POR QUE EXISTE ──
// O app tem CINCO bancos IDB independentes (`iptv-local`, `iptv-catalog`,
// `iptv-series-info`, `iptv-vod-info`, `iptv-sync`), e cada um tinha o seu
// próprio `getDb()` escrito à mão, com a MESMA armadilha:
//
//   if (!_dbPromise) { _dbPromise = openDB(...) }
//   return _dbPromise
//
// A promise da abertura ficava guardada e nunca era solta. Uma rejeição (quota
// estourada, storage evictado pelo webOS, modo privado) ficava CACHEADA: toda
// chamada seguinte recebia a MESMA rejeição e aquele banco morria em silêncio
// pelo resto da sessão. No `iptv-sync` isso é especialmente caro — é a fila que
// leva progresso e favoritos pro servidor, então o sync cross-device parava sem
// um único sinal.
//
// Consertar cinco vezes garantiria a sexta cópia na próxima feature. Este
// módulo é o molde único: quem precisa de um banco chama `makeDbOpener` e
// recebe o comportamento correto por CONSTRUÇÃO.
//
// ── O QUE O MOLDE GARANTE ──
//  1. SOLTA a promise quando a abertura falha (a próxima chamada pode reabrir).
//  2. GUARDA DE IDENTIDADE (`current === attempt`): uma tentativa velha que
//     rejeita tarde não derruba a conexão de uma tentativa nova bem-sucedida.
//     Sem ela, soltar a promise abre uma corrida pior que o bug original.
//  3. JANELA DE ESPERA antes de reabrir, pra uma rajada de chamadas não virar
//     uma rajada de aberturas falhas num device com IDB permanentemente morto.
//  4. REGISTRO: todo opener criado entra numa lista, e é isso que permite ao
//     "Limpar dados" alcançar os CINCO bancos em vez de um só.
//
// ── COMPAT Chr38 (piso do app de TV) ──
// Só closures e `Date.now`. Sem `Proxy`, sem `class` com campos privados, sem
// `WeakRef`. O `openDB` vem do `idb-native` (wrapper nativo), nunca da lib.

import type { DBSchema, IDBPDatabase, OpenDBCallbacks } from 'idb'

import { deleteDB, openDB } from './idb-native'

/**
 * Janela padrão antes de tentar REABRIR um banco depois de uma falha.
 *
 * O número saiu da medição do `local-db` (o banco com mais call-sites: 35, dos
 * quais ~10 disparam juntos numa entrada de tela). A rajada cabe no orçamento
 * de transição de 200-300ms, nunca >400 (docs/specs/ux-tv.md), então qualquer
 * janela acima disso a colapsa numa ÚNICA tentativa — 30s dá 75x de folga. E é
 * curta o bastante pra recuperação ser perceptível dentro da sessão: quem usou
 * "Limpar dados" ou cujo SO devolveu quota volta a gravar sem reiniciar o app.
 * Teto numa sessão longa de 2h: 2*3600/30 = 240 tentativas por banco, contra o
 * ilimitado de antes.
 *
 * Alternativas descartadas, registradas de propósito:
 *  • 3h (o `MIN_AGGRESSIVE_INTERVAL_MS` do idb-quota-manager): aquele número
 *    guarda uma operação DESTRUTIVA e cara (eviction de LRU), onde repetir
 *    machuca. Reabrir um banco é barato e idempotente; com 3h, quem liberou
 *    espaço seguiria quebrado pelo resto da sessão — o mesmo defeito "morto em
 *    silêncio" que este módulo existe pra remover, só que com pavio longo.
 *  • Backoff exponencial: exige estado por tentativa e política de reset, e o
 *    teto que compraria não é necessário (240/2h já é baixo). O que custa é a
 *    RAJADA, e qualquer janela >400ms já a colapsa por inteiro. Mais estado no
 *    piso Chr38 é mais superfície de bug sem ganho medido.
 */
export const DB_REOPEN_COOLDOWN_MS = 30_000

export interface DbOpener<T> {
  /** Nome do banco (o mesmo passado ao `indexedDB.open`). */
  readonly name: string
  /** A conexão. Rejeita quando o IDB está indisponível — o caller trata. */
  get(): Promise<IDBPDatabase<T>>
  /**
   * Fecha a conexão aberta (se houver) e zera o estado, inclusive a janela de
   * espera — a próxima chamada tenta abrir NA HORA. NÃO apaga o banco.
   */
  dispose(): Promise<void>
  /** `dispose()` + apaga o banco do disco. */
  destroy(): Promise<void>
}

export interface DbOpenerConfig<T> {
  name: string
  version: number
  upgrade?: OpenDBCallbacks<T>['upgrade']
  /** Só pra teste; produção usa o padrão. */
  cooldownMs?: number
}

/** Todos os openers criados, na ordem de criação. É o que dá ao "Limpar dados"
 *  alcance sobre os CINCO bancos — e o que faz o SEXTO entrar sozinho. */
const registry: DbOpener<never>[] = []

/** Rejeição já "tratada" (catch no-op), pra reservir a falha durante a janela
 *  sem gerar unhandled rejection num motor antigo se algum caller esquecer. */
function rejectedWith<T>(error: unknown): Promise<IDBPDatabase<T>> {
  const p = Promise.reject(error) as Promise<IDBPDatabase<T>>
  p.catch(() => {})
  return p
}

export function makeDbOpener<T extends DBSchema>(config: DbOpenerConfig<T>): DbOpener<T> {
  const cooldownMs = config.cooldownMs ?? DB_REOPEN_COOLDOWN_MS
  let current: Promise<IDBPDatabase<T>> | null = null
  let failure: { at: number; error: unknown } | null = null

  const get = (): Promise<IDBPDatabase<T>> => {
    if (current) return current
    // Falhou há pouco: devolve a mesma rejeição sem tocar o disco.
    if (failure && Date.now() - failure.at < cooldownMs) return rejectedWith<T>(failure.error)
    let attempt: Promise<IDBPDatabase<T>>
    // Solta ESTA conexão (se ainda for a corrente): a próxima chamada reabre.
    // `attempt` já está atribuída quando os eventos abaixo disparam — eles só
    // existem depois do `onsuccess`.
    const soltar = (): void => {
      if (current === attempt) current = null
    }
    // O `openDB` do idb-native já embrulha o `indexedDB.open` num executor de
    // Promise, então um throw síncrono de lá SAI COMO REJEIÇÃO e cai no
    // `.catch` abaixo. Este try é a guarda da FRONTEIRA: custa nada e mantém o
    // contrato ("get sempre devolve promise") mesmo se alguém trocar a fonte do
    // `openDB` por uma que lance direto.
    try {
      attempt = openDB<T>(config.name, config.version, {
        ...(config.upgrade ? { upgrade: config.upgrade } : {}),
        // OUTRO documento quer apagar ou migrar este banco (review do
        // `181b1f81`, P2). Sem fechar, o delete/abertura dele pendura SEM
        // PRAZO. Na TV não é alcançável (um documento só); na web com várias
        // abas é. Fecha na hora (síncrono, pelo alvo do evento) E solta a
        // referência: fechar e continuar guardando a conexão morta seria a mesma
        // classe de defeito que este módulo existe pra fechar — a próxima chamada
        // receberia uma conexão fechada pra sempre.
        blocking: (_versaoAtual, _versaoPedida, event) => {
          soltar()
          ;(event.target as IDBDatabase | null)?.close()
        },
        // O navegador fechou a conexão por conta própria: solta, pra reabrir.
        terminated: () => soltar(),
      }) as Promise<IDBPDatabase<T>>
    } catch (error: unknown) {
      failure = { at: Date.now(), error }
      return rejectedWith<T>(error)
    }
    current = attempt
    // SOLTA a promise em caso de falha — é isto que impede a rejeição de virar
    // permanente. A comparação `current === attempt` evita que uma tentativa
    // VELHA que rejeita tarde derrube a conexão de uma tentativa NOVA que já
    // deu certo: sem ela, soltar a promise cria uma corrida pior que o bug.
    attempt.catch((error: unknown) => {
      failure = { at: Date.now(), error }
      if (current === attempt) current = null
    })
    return attempt
  }

  const dispose = async (): Promise<void> => {
    // DESLIGA SÍNCRONO, antes de qualquer await. Duas razões:
    //
    //  1. Se a abertura ainda está PENDENTE, esperar por ela aqui prenderia o
    //     `dispose` (e o "Limpar dados" junto) até o IDB responder — que pode
    //     ser nunca, num device travado. Soltar a referência primeiro deixa a
    //     próxima chamada abrir uma conexão nova em vez de herdar a velha.
    //  2. É o que torna a GUARDA DE IDENTIDADE necessária de verdade: a partir
    //     daqui a tentativa velha continua viva e pode rejeitar DEPOIS de uma
    //     nova já ter dado certo. Sem `current === attempt` no catch dela, essa
    //     rejeição atrasada derrubaria a conexão boa.
    const pending = current
    current = null
    failure = null
    if (!pending) return
    // NÃO aguarda a abertura: ela pode estar PENDENTE e nunca responder num
    // device com o storage travado, e prender o "Limpar dados" nela trocaria um
    // defeito silencioso por um travamento na cara do usuário. O fechamento
    // fica AGENDADO pra quando (e se) ela resolver, então nenhuma conexão vaza.
    // O `.catch` cobre a abertura que rejeita: não há conexão pra fechar, e o
    // erro não pode escapar como unhandled rejection.
    void pending
      .then((db) => {
        db.close()
      })
      .catch(() => {})
  }

  const opener: DbOpener<T> = {
    name: config.name,
    get,
    dispose,
    async destroy() {
      await dispose()
      // Uma volta de microtask pro fechamento agendado no `dispose` rodar no
      // caso NORMAL (abertura já resolvida) antes do delete — assim o
      // `deleteDatabase` não cai no estado `blocked` à toa. Abertura pendente
      // não trava o delete: o navegador resolve o bloqueio quando ela fechar.
      await Promise.resolve()
      await deleteDB(config.name)
    },
  }
  registry.push(opener as unknown as DbOpener<never>)
  return opener
}

/** Nomes dos bancos registrados, na ordem de criação. Existe pro gate provar
 *  que os CINCO passam por aqui — e para o teste não depender de uma lista
 *  escrita à mão, que nasceria subconjunto. */
export function registeredDbNames(): string[] {
  return registry.map((o) => o.name)
}

/**
 * Bancos que podem existir NO DISCO, pelo nome — o alcance do "Limpar dados".
 *
 * ── Por que uma lista, se já existe o registro ──
 * Os dois respondem perguntas DIFERENTES:
 *  • o REGISTRO diz quais conexões ESTE documento tem abertas — e elas precisam
 *    ser fechadas antes do delete, porque o idb-native não trata `onblocked`
 *    (um delete com conexão aberta pendura).
 *  • ESTA LISTA diz quais bancos podem estar no disco. Apagar por NOME não
 *    precisa do módulo carregado.
 * Só o registro não bastaria: ele só conhece o banco cujo módulo JÁ FOI
 * IMPORTADO nesta sessão. Hoje os quatro carregam no boot (providers.tsx →
 * long-session-cleaner → idb-quota-manager → as stores; local-db → sync →
 * outbox), então o buraco é LATENTE — mas as telas já são lazy, e no dia em que
 * um desses imports virar lazy o "Limpar dados" encolheria calado. Um banco de
 * versão anterior cujo módulo foi removido também seguiria no disco pra sempre.
 *
 * `iptv-local` NÃO está aqui: é do local-db, cujo `hardResetLocalDb` o fecha e
 * apaga (a migração dele pro molde é follow-up; ver idb-opener.gate.test.ts).
 *
 * Guardada nos DOIS sentidos pelo gate: todo `makeDbOpener` do código tem que
 * estar aqui (pega o banco novo esquecido) e todo nome daqui tem que existir no
 * código (pega o nome que ficou pra trás).
 */
export const MANAGED_DB_NAMES: readonly string[] = [
  'iptv-catalog',
  'iptv-series-info',
  'iptv-vod-info',
  'iptv-sync',
]

/**
 * Prazo do `destroyAllDbs`. Quem chama é o "Limpar dados", cujo handler tem uma
 * invariante documentada: TEM que chegar ao `location.replace`, senão a tela de
 * Configurações fica viva sem o diálogo e o foco do D-pad fica órfão pra sempre.
 * Um delete BLOQUEADO (conexão que não fecha, e o idb-native não tem
 * `onblocked`) não rejeita — pendura, e `try/catch` não pega pendura.
 *
 * O número: o aparelho mediu ABERTURA dos cinco bancos em 2-9ms; deleção NÃO foi
 * medida, então isto é um teto de engenharia, não uma medição. 3s dá >300x de
 * folga sobre a ordem de grandeza medida e fica abaixo do que quem acabou de
 * confirmar uma ação destrutiva de último recurso lê como travamento — o
 * documento é trocado logo depois de qualquer jeito. Estourado, o reset SEGUE: o
 * delete pendente continua na fila do navegador, e o que não foi apagado aparece
 * em `failed`.
 */
export const DESTROY_ALL_TIMEOUT_MS = 3_000

/**
 * Apaga TODOS os bancos gerenciados. É o alcance do "Limpar dados".
 *
 * ⚠️ Antes disto a escotilha alcançava 1 de 5: `hardResetLocalDb` só apagava o
 * `iptv-local`, então quem usava "Limpar dados" recuperava favoritos e progresso
 * e seguia com catálogo, fichas e outbox mortos até reiniciar o app.
 *
 * Em PARALELO (um banco lento não segura os outros) e com PRAZO (ver acima). Não
 * lança: um banco que recusa não impede os outros. Devolve o que ficou pra trás.
 *
 * COMPAT Chr38: sem spread de Set, sem `Promise.allSettled` (Chr76), sem
 * `.finally` (Chr63).
 */
export async function destroyAllDbs(
  opts: { timeoutMs?: number } = {},
): Promise<{ failed: string[]; timedOut: boolean }> {
  const nomes: string[] = []
  for (const n of MANAGED_DB_NAMES) if (nomes.indexOf(n) < 0) nomes.push(n)
  for (const o of registry) if (nomes.indexOf(o.name) < 0) nomes.push(o.name)
  return destroyDbsByName(nomes, opts)
}

/**
 * Apaga os bancos NOMEADOS — o mecanismo do `destroyAllDbs`, exposto pra quem
 * precisa apagar uma lista específica (a nova tentativa do "Limpar dados" no
 * boot seguinte, em hard-reset-pendente.ts). Uma implementação só: o prazo, o
 * paralelismo e o fechar-antes-de-apagar não existem em duas cópias.
 *
 * Banco com opener registrado NESTE documento: fecha a conexão antes (sem
 * `onblocked` no idb-native, delete com conexão aberta pendura). Sem opener: o
 * módulo nem carregou, não há conexão — apaga direto pelo nome.
 */
export async function destroyDbsByName(
  nomesPedidos: readonly string[],
  opts: { timeoutMs?: number } = {},
): Promise<{ failed: string[]; timedOut: boolean }> {
  const timeoutMs = opts.timeoutMs ?? DESTROY_ALL_TIMEOUT_MS
  const nomes: string[] = []
  for (const n of nomesPedidos) if (nomes.indexOf(n) < 0) nomes.push(n)

  const pendentes: Record<string, true> = {}
  for (const n of nomes) pendentes[n] = true
  const failed: string[] = []

  const trabalho = Promise.all(
    nomes.map(async (nome) => {
      const aberto = registry.filter((o) => o.name === nome)[0]
      try {
        // Com conexão NESTE documento: fecha antes (senão o delete bloqueia).
        // Sem: o módulo nem carregou, não há conexão — apaga direto pelo nome.
        if (aberto) await aberto.destroy()
        else await deleteDB(nome)
      } catch {
        failed.push(nome)
      }
      delete pendentes[nome]
    }),
  )

  let timer: ReturnType<typeof setTimeout> | undefined
  const prazo = new Promise<'prazo'>((resolve) => {
    timer = setTimeout(() => resolve('prazo'), timeoutMs)
  })
  const venceu = await Promise.race([trabalho.then(() => 'ok' as const), prazo])
  if (timer !== undefined) clearTimeout(timer)
  if (venceu === 'prazo') return { failed: failed.concat(Object.keys(pendentes)), timedOut: true }
  return { failed, timedOut: false }
}
